import PaginatedTable from '../../components/ui/PaginatedTable';
import PermissionButton from "../../components/ui/PermissionButton";
import ReportExportButton from '../../components/ui/ReportExportButton';
import FinancialScopeNotice from '../../components/ui/FinancialScopeNotice';
import TableActions from '../../components/ui/TableActions';
import { useEffect, useMemo, useState } from "react";
import DashboardLayout from "../Layouts/DashboardLayout";
import ProtectedRoute from "../Components/ProtectedRoute";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Eye,
  Download,
} from "lucide-react";

export default function ProfitLoss() {
  const [selected, setSelected] = useState(null);
  const [report, setReport] = useState({
    income: [],
    expenses: [],
    total_income: 0,
    total_expense: 0,
    net_profit: 0,
  });

  useEffect(() => {
    loadReport();
  }, []);

  async function loadReport() {
    try {
      const res = await fetch("/api/profit-loss", {
        credentials: "include",
      });

      const data = await res.json();

      if (data.status === "success") {
        setReport(data);
      }
    } catch (err) {
      console.log(err);
    }
  }

  const totalIncome = report.total_income;
  const totalExpense = report.total_expense;
  const netProfit = report.net_profit;

  return (
    <ProtectedRoute permission="accounting.profit_loss">
      <DashboardLayout>
      <FinancialScopeNotice/>
        <div className="space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-serif">Profit & Loss</h1>

              <p className="text-sm text-gray-400 mt-1">Income Statement</p>
            </div>

            <ReportExportButton viewPermission="accounting.profit_loss" className="flex items-center gap-2 px-5 py-2 rounded-xl bg-amber-400 text-black">
              <Download size={18} />
              Export
            </ReportExportButton>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <KPI
              title="Total Income"
              value={totalIncome}
              color="text-emerald-400"
              icon={TrendingUp}
            />

            <KPI
              title="Total Expense"
              value={totalExpense}
              color="text-rose-400"
              icon={TrendingDown}
            />

            <KPI
              title="Net Profit"
              value={netProfit}
              color="text-amber-400"
              icon={DollarSign}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
              <div className="px-6 py-4 border-b border-white/10">
                <h3 className="font-serif text-lg">Income</h3>
              </div>

              <PaginatedTable className="w-full">
                <tbody>
                  {report.income.map((item) => (
                    <tr
                      key={item.id}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="px-6 py-4">
                        {item.code} - {item.account}
                      </td>

                      <td className="text-right text-emerald-400">
                        ₹{Number(item.amount).toLocaleString("en-IN")}
                      </td>

                      <td className="w-20 text-center"><TableActions>
                        <PermissionButton permissionModule="accounts"
                          onClick={() => setSelected(item)}
                          className="hover:text-amber-400"
                        >
                          <Eye size={18} />
                        </PermissionButton>
                      </TableActions></td>
                    </tr>
                  ))}

                  <tr className="border-t border-white/10 font-semibold">
                    <td className="px-6 py-4">Total Income</td>

                    <td className="text-right text-emerald-400">
                      ₹{totalIncome.toLocaleString()}
                    </td>

                    <td></td>
                  </tr>
                </tbody>
              </PaginatedTable>
            </div>

            <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden">
              <div className="px-6 py-4 border-b border-white/10">
                <h3 className="font-serif text-lg">Expenses</h3>
              </div>

              <PaginatedTable className="w-full">
                <tbody>
                  {report.expenses.map((item) => (
                    <tr
                      key={item.id}
                      className="border-t border-white/10 hover:bg-white/5"
                    >
                      <td className="px-6 py-4">
                        {item.code} - {item.account}
                      </td>

                      <td className="text-right text-rose-400">
                        ₹{Number(item.amount).toLocaleString("en-IN")}
                      </td>

                      <td className="w-20 text-center"><TableActions>
                        <PermissionButton permissionModule="accounts"
                          onClick={() => setSelected(item)}
                          className="hover:text-amber-400"
                        >
                          <Eye size={18} />
                        </PermissionButton>
                      </TableActions></td>
                    </tr>
                  ))}

                  <tr className="border-t border-white/10 font-semibold">
                    <td className="px-6 py-4">Total Expense</td>

                    <td className="text-right text-rose-400">
                      ₹{totalExpense.toLocaleString()}
                    </td>

                    <td></td>
                  </tr>
                </tbody>
              </PaginatedTable>
            </div>
          </div>

          <div
            className={`rounded-2xl border p-8 ${
              netProfit >= 0
                ? "bg-emerald-500/10 border-emerald-500/20"
                : "bg-red-500/10 border-red-500/20"
            }`}
          >
            <p className="text-xs uppercase tracking-widest text-gray-400">
              Net Result
            </p>

            <h2 className="mt-4 text-5xl font-serif">
              ₹{netProfit.toLocaleString()}
            </h2>

            <p className="mt-3 text-sm text-gray-400">
              {netProfit > 0
                ? "Business is running in profit."
                : netProfit < 0
                  ? "Business is running in loss."
                  : "Business is at break-even."}
            </p>
          </div>

          {selected && (
            <PLModal item={selected} onClose={() => setSelected(null)} />
          )}
        </div>
      </DashboardLayout>
    </ProtectedRoute>
  );
}

function KPI({ title, value, color, icon: Icon }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-6">
      <div className="flex justify-between">
        <p className="text-xs uppercase tracking-widest text-gray-400">
          {title}
        </p>

        <Icon size={18} />
      </div>

      <h2 className={`mt-4 text-3xl font-serif ${color}`}>
        ₹{value.toLocaleString()}
      </h2>
    </div>
  );
}

function PLModal({ item, onClose }) {
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetch(`/api/profit-loss/${item.id}`, {
      credentials: "include",
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.status === "success") {
          setTransactions(data.transactions);
        }
      });
  }, [item]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-center items-center z-50">
      <div className="w-full max-w-5xl rounded-2xl bg-[#09090B] border border-white/10">
        <div className="flex justify-between items-center px-8 py-5 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-serif">{item.account}</h2>

            <p className="text-gray-400 text-sm">Profit & Loss Details</p>
          </div>

          <PermissionButton permissionModule="accounts" onClick={onClose}>✕</PermissionButton>
        </div>

        <div className="grid grid-cols-3 gap-6 p-8">
          <div className="rounded-xl bg-white/5 p-5">
            <p className="text-xs text-gray-400">Account</p>

            <h3 className="mt-3 text-xl">{item.account}</h3>
          </div>

          <div className="rounded-xl bg-white/5 p-5">
            <p className="text-xs text-gray-400">Code</p>

            <h3 className="mt-3 text-xl">{item.code}</h3>
          </div>

          <div className="rounded-xl bg-white/5 p-5">
            <p className="text-xs text-gray-400">Amount</p>

            <h3 className="mt-3 text-xl text-amber-400">
              ₹{Number(item.amount).toLocaleString("en-IN")}
            </h3>
          </div>
        </div>

        <div className="px-8 pb-8">
          <PaginatedTable className="w-full">
            <thead className="text-gray-400">
              <tr>
                <th className="text-left py-3">Date</th>
                <th className="text-left">Voucher</th>
                <th className="text-left">Reference</th>
                <th className="text-right">Debit</th>
                <th className="text-right">Credit</th>
              </tr>
            </thead>

            <tbody>
              {transactions.map((t) => (
                <tr key={t.id} className="border-t border-white/10">
                  <td className="py-4">{t.date}</td>

                  <td>{t.voucher}</td>

                  <td>{t.reference}</td>

                  <td className="text-right text-emerald-400">
                    ₹{Number(t.debit).toLocaleString("en-IN")}
                  </td>

                  <td className="text-right text-red-400">
                    ₹{Number(t.credit).toLocaleString("en-IN")}
                  </td>
                </tr>
              ))}
            </tbody>
          </PaginatedTable>
        </div>

        <div className="border-t border-white/10 px-8 py-5 flex justify-end">
          <PermissionButton permissionModule="accounts"
            onClick={onClose}
            className="px-6 py-2 rounded-xl bg-amber-400 text-black"
          >
            Close
          </PermissionButton>
        </div>
      </div>
    </div>
  );
}
